// ============================================================================
// ATTRIBUTE PRIVACY UTILS
// ============================================================================

function makePseudonymiser(prefix = 'Person') {
  const map = new Map();
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let counter = 0;

  return {
    get(originalValue) {
      const key = String(originalValue ?? '').trim();
      if (!key) return key;
      if (!map.has(key)) {
        const idx = counter++;
        let label = '';
        let n = idx;
        do {
          label = alphabet[n % 26] + label;
          n = Math.floor(n / 26) - 1;
        } while (n >= 0);
        map.set(key, `${prefix} ${label}`);
      }
      return map.get(key);
    },
    getLegend() {
      const legend = {};
      map.forEach((pseudo, original) => {
        legend[pseudo] = original;
      });
      return legend;
    },
  };
}

export function normalizeFieldName(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_\-]+/g, '');
}

export function buildFieldAliasMap(fields = []) {
  const aliasMap = {};
  (fields || []).forEach((field) => {
    const normalized = normalizeFieldName(field);
    if (!normalized) return;
    if (!aliasMap[normalized]) {
      aliasMap[normalized] = field;
    }
  });
  return aliasMap;
}

export function resolveFieldName(field, aliasMap = {}) {
  return aliasMap[normalizeFieldName(field)] || field;
}

export function parseAttributePrivacy(raw) {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
  } catch (_) {}
  return {};
}

export function applySampleItemPrivacy(rows, privacyRules) {
  if (!Array.isArray(rows) || rows.length === 0 || !privacyRules || Object.keys(privacyRules).length === 0) {
    return { rows, anonymizationMap: {}, excludedFields: [], anonymizedFields: [] };
  }

  const allFieldNames = [];
  const seen = new Set();

  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key);
        allFieldNames.push(key);
      }
    });
  });

  Object.keys(privacyRules || {}).forEach((key) => {
    if (!seen.has(key)) {
      allFieldNames.push(key);
    }
  });

  const aliasMap = buildFieldAliasMap(allFieldNames);

  const resolvedRules = {};
  Object.entries(privacyRules || {}).forEach(([field, rule]) => {
    const resolvedField = resolveFieldName(field, aliasMap);
    resolvedRules[resolvedField] = rule;
  });

  const excludedFields = [];
  const anonymizedFields = [];
  const pseudonymisers = {};

  Object.entries(resolvedRules).forEach(([field, rule]) => {
    if (rule === 'exclude') {
      excludedFields.push(field);
    } else if (rule === 'anonymize') {
      anonymizedFields.push(field);
      pseudonymisers[field] = makePseudonymiser(field);
    }
  });

  const sanitisedRows = rows.map((row) => {
    const out = {};
    Object.entries(row || {}).forEach(([key, value]) => {
      const resolvedKey = resolveFieldName(key, aliasMap);
      const rule = resolvedRules[resolvedKey];
      if (rule === 'exclude') {
        return;
      }
      if (rule === 'anonymize' && pseudonymisers[resolvedKey]) {
        out[resolvedKey] = pseudonymisers[resolvedKey].get(value);
        return;
      }
      out[resolvedKey] = value;
    });
    return out;
  });

  const anonymizationMap = {};
  anonymizedFields.forEach((field) => {
    anonymizationMap[field] = pseudonymisers[field].getLegend();
  });

  return { rows: sanitisedRows, anonymizationMap, excludedFields, anonymizedFields };
}

export function AttributePrivacyPanel({ excludedFields = [], anonymizedFields = [], anonymizationMap = {} }) {
  const React = require('react');
  const [expanded, setExpanded] = React.useState(false);

  const hasAny = excludedFields.length > 0 || anonymizedFields.length > 0;
  if (!hasAny) return null;

  const panelStyle = {
    margin: '8px 10px 0',
    background: '#FFFAE6',
    border: '1px solid #FFD700',
    borderRadius: 6,
    fontSize: 12,
    color: '#172B4D',
    overflow: 'hidden',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 12px',
    cursor: 'pointer',
    userSelect: 'none',
    fontWeight: 600,
  };

  const bodyStyle = {
    padding: '0 12px 10px',
    borderTop: '1px solid #FFD700',
  };

  const tagStyle = (color, bg) => ({
    display: 'inline-block',
    padding: '2px 7px',
    borderRadius: 3,
    fontSize: 11,
    fontWeight: 600,
    color,
    background: bg,
    marginRight: 4,
    marginBottom: 2,
  });

  return (
    <div style={panelStyle}>
      <div style={headerStyle} onClick={() => setExpanded((v) => !v)}>
        <span>🔒</span>
        <span>AI Data Privacy Applied</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#5E6C84', fontWeight: 400 }}>
          {excludedFields.length > 0 && `${excludedFields.length} excluded`}
          {excludedFields.length > 0 && anonymizedFields.length > 0 && ' · '}
          {anonymizedFields.length > 0 && `${anonymizedFields.length} anonymized`}
        </span>
        <span style={{ fontSize: 10, color: '#5E6C84' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={bodyStyle}>
          {excludedFields.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: '#BF2600' }}>Excluded from AI prompts:</div>
              <div>
                {excludedFields.map((f) => (
                  <span key={f} style={tagStyle('#BF2600', '#FFEBE6')}>{f}</span>
                ))}
              </div>
            </div>
          )}

          {anonymizedFields.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: '#0747A6' }}>Anonymized (pseudonym → real value):</div>
              {anonymizedFields.map((field) => {
                const legend = anonymizationMap[field] || {};
                const entries = Object.entries(legend);
                return (
                  <div key={field} style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, marginBottom: 3 }}>{field}:</div>
                    {entries.length === 0 ? (
                      <span style={{ color: '#5E6C84' }}>No values encountered</span>
                    ) : (
                      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '2px 8px 2px 0', color: '#0747A6', borderBottom: '1px solid #B3D4FF' }}>AI Pseudonym</th>
                            <th style={{ textAlign: 'left', padding: '2px 0', color: '#0747A6', borderBottom: '1px solid #B3D4FF' }}>Real Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entries.map(([pseudo, real]) => (
                            <tr key={pseudo}>
                              <td style={{ padding: '2px 8px 2px 0', color: '#0052CC', fontWeight: 600 }}>{pseudo}</td>
                              <td style={{ padding: '2px 0' }}>{real}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
